// FieldIQ — BuildIQ construction-service client (crew persona).
// Same platform JWT as the rest of FieldIQ; the BuildIQ service resolves the
// crew persona from user_service_access and serves the pricing-REDACTED job
// DB. Writes go through the server's crew_merge, which hard-limits crew to
// schedule / checklists / tasks / time / log on existing jobs — this client
// can never create, delete, reorder or price a job even if it tried.

import { getToken } from "./api";

export const BUILDIQ_URL =
  process.env.REACT_APP_BUILDIQ_URL ||
  "https://buildiq.hyveappliedintelligence.com";

// Canonical /api/fn/* service paths — the one path convention shipping code uses.
const STATE_PATH = "/api/fn/state";
const PHOTOS_PATH = "/api/fn/photos";
const ASSETS_PATH = "/api/fn/assets";

export const biqFetch = async (path, opts = {}) => {
  const headers = { Authorization: `Bearer ${getToken()}` };
  if (opts.method && opts.method !== "GET")
    headers["Content-Type"] = "application/json";
  const res = await fetch(`${BUILDIQ_URL}${path}`, {
    ...opts,
    headers: { ...headers, ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    return { error: data.detail || data.error || `HTTP ${res.status}` };
  return data;
};

// Redacted job DB: {jobs, order, deleted, events, redacted:true}
export const biqGetState = () => biqFetch(STATE_PATH);

// Redacted asset registry: {assets, deleted, redacted:true}. The crew persona
// gets the maintenance plan (trade/vendor/cadence) but NEVER the lease block —
// lease dollars are office-only, stripped server-side (redact_assets_for_crew).
export const biqGetAssets = () => biqFetch(ASSETS_PATH);

// Push crew-editable slices of existing jobs. Each patch carries the whole
// current value of the fields it touches (the server replaces per-field,
// except logs which union). Always stamp updated so LWW favors this edit.
export const biqCrewPush = (jobsById) =>
  biqFetch(STATE_PATH, {
    method: "PUT",
    body: JSON.stringify({ jobs: jobsById, redacted: true }),
  });

export const biqListPhotos = (jobId) =>
  biqFetch(`${PHOTOS_PATH}?list=${encodeURIComponent(jobId)}`);

export const biqUploadPhoto = (jobId, meta, imgB64, thumbB64) =>
  biqFetch(PHOTOS_PATH, {
    method: "POST",
    body: JSON.stringify({ action: "upload", jobId, meta, imgB64, thumbB64 }),
  });

export const biqPhotoUrl = (photoId, thumb = false) =>
  `${BUILDIQ_URL}${PHOTOS_PATH}?${thumb ? "thumb" : "img"}=${photoId}`;

export const biqUid = () =>
  "x" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

// File → client-compressed JPEG base64 pair (max 1600px full / 320px thumb),
// matching what the office app uploads.
export const compressPhoto = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = (max) => {
        const r = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * r));
        canvas.height = Math.max(1, Math.round(img.height * r));
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL("image/jpeg", 0.82).split(",")[1];
      };
      resolve({
        imgB64: scale(1600),
        thumbB64: scale(320),
        w: img.width,
        h: img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
